import { Dispatch, SetStateAction } from 'react'

export enum PreApplicationStep {
  COMPANY_SIZE = 'COMPANY_SIZE',
  MONTHLY_SPEND = 'MONTHLY_SPEND',
  UNQUALIFIED = 'UNQUALIFIED',
  SELF_SERVICE_QUALIFIED = 'SELF_SERVICE_QUALIFIED',
  SALES_QUALIFIED = 'SALES_QUALIFIED',
  DEMO_REQUESTED = 'DEMO_REQUESTED',
  INTERESTED_PRODUCTS = 'INTERESTED_PRODUCTS',
}

export type RedirectToPath =
  | PreApplicationStep.SELF_SERVICE_QUALIFIED
  | PreApplicationStep.DEMO_REQUESTED

export enum RoutingDirection {
  SALES_QUALIFIED = 'sales_qualified',
  SALES_OPTIONAL = 'sales_optional',
  SELF_SERVICE_NO_PREQUAL = 'self_service_no_prequal',
  SELF_SERVICE_PREQUAL = 'self_service_prequal',
  UNQUALIFIED = 'unqualified',
}

export enum MadKuduSegment {
  BAD = 'bad',
  LOW = 'low',
  MEDIUM = 'medium',
  GOOD = 'good',
  VERY_GOOD = 'very good',
}

export enum LeadExperiment {
  SALES_NEW_VARIANT = '2022-04 sales-routed new variant',
  SALES_CONTROL = '2022-04 sales control group',
  SEP_2023_CRO = 'sep-2023-cro-treatment',
  ContactSalesScaledSDR = 'july-2024-contact-sales-scaled-sdr-chilipiper',
  ContactSalesStandardSDR = 'july-2024-contact-sales-sdr-chilipiper',
  QualifiedChatBotControl = 'nov-2024-qualified-chat-bot-control',
  QualifiedChatBotTreatment = 'nov-2024-qualified-chat-bot-treatment',
  SelfServeCopyH1 = 'nov-2024-self-serve-copy-h1',
  SelfServeCopyH2 = 'nov-2024-self-serve-copy-h2',
  SignUpButtonCopyControl = 'nov-2024-sign-up-button-copy-control',
  SignUpButtonCopyTreatment = 'nov-2024-sign-up-button-copy-treatment',
  /**
   * When Ramp is undergoing an endpoint scan (i.e. a bad actor enumerating public
   * endpoints to extract information), we turn on a server-side flag that replaces
   * the "user already exists" error code with a fake payload. That flag also attaches
   * this experiment label to the lead response so the frontend knows to bypass the
   * normal prequal flow and route the user straight to self-service sign-up,
   * preventing them from entering a hung state.
   */
  EnablePrequalBypass = 'enable_prequal_bypass',
  /**
   * Control arm of ramp.com's `prequal-direct-signup-august-2026` experiment. Treatment
   * leads are created by ramp.com as `move_prequal_post_login`; control users land on
   * /qualification, which tags the leads it creates with this value.
   */
  MovePrequalPostLoginControl = 'move_prequal_post_login_control',
}

export type ApiLeadModel = Pick<
  LeadModel,
  'id' | 'skipped_prequal' | 'routing_direction' | 'experiments'
>

export type LeadModel = {
  readonly id: string
  readonly company_email?: string

  readonly employee_count?: string
  readonly has_employee_count?: boolean

  readonly estimated_monthly_spend?: string
  readonly has_estimated_monthly_spend?: boolean

  readonly bank_balance?: string
  readonly has_bank_balance?: boolean

  readonly company_name?: string
  readonly has_company_name?: boolean

  readonly first_name?: string
  readonly has_first_name?: boolean

  readonly last_name?: string
  readonly has_last_name?: boolean

  readonly requested_demo?: boolean
  readonly interested_in_corporate_cards?: boolean
  readonly interested_in_reimbursements?: boolean
  readonly interested_in_bill_payments?: boolean
  readonly interested_in_flex?: boolean
  readonly interested_in_procurement?: boolean
  readonly interested_in_treasury?: boolean
  readonly interested_in_accounts_receivable?: boolean
  readonly interested_in_stack?: boolean
  readonly interested_in_token_spend_management?: boolean

  readonly utm_campaign?: string
  readonly utm_campaign_at?: string
  readonly utm_source?: string
  readonly utm_medium?: string
  readonly utm_term?: string
  readonly utm_content?: string
  readonly promo?: string
  readonly referral_code?: string

  readonly initial_utm_campaign?: string
  readonly initial_utm_campaign_at?: string
  readonly initial_utm_source?: string
  readonly initial_utm_medium?: string
  readonly initial_utm_term?: string
  readonly initial_utm_content?: string
  readonly initial_promo?: string
  readonly initial_referral_code?: string

  readonly skipped_prequal?: boolean
  readonly routing_direction?: RoutingDirection
  readonly score?: MadKuduSegment
  readonly clearbit_employee_count?: number
  readonly is_treatment?: boolean
  readonly gclid?: string | null
  readonly ramp_attid?: string | null
  readonly experiment?: LeadExperiment
  readonly experiments?: LeadExperiment[]
  readonly sales_demo_booked_at?: string
  readonly calendar_load_ms?: number
  readonly calendar_booking_url?: string

  readonly segment_device_uuid?: string
  readonly discovery_method?: string

  readonly sales_meeting_eligible?: boolean
  readonly router_id?: string | null

  isHydrated?: boolean
}

export type LeadContextProps = {
  lead: LeadModel | null
  setLead: Dispatch<SetStateAction<LeadModel | null>>
  setHydratedLead: Dispatch<SetStateAction<LeadModel | null>>
  setIsLeadHydrated: Dispatch<SetStateAction<boolean>>
  isLeadHydrated: boolean
}
